/** @file gizmoView: every tool's gizmo meshes, built once and updated in place inside the render loop. */
import { Vector2 } from '@babylonjs/core/Maths/math.vector.js';
import type { Scene } from '@babylonjs/core/scene.js';
import { arcSector } from './arcSector';
import type { GizmoFrame, HandleSet } from './gizmoFrame';
import { gizmoNodes } from './gizmoNodes';
import { moveHandles } from './moveHandles';
import { rotateHandles } from './rotateHandles';
import { scaleHandles } from './scaleHandles';

/** `update(frame)` once per frame before `scene.render()` (null hides the gizmo); `dispose` once. */
export type GizmoView = { update(f: GizmoFrame | null): void; dispose(): void };

/**
 * The move, rotate and scale handle sets and the swept-arc sector, all built at creation (a tool switch only turns
 * sets on and off; nothing is created or disposed per frame). Each frame places the gizmo nodes, styles the active
 * set and the arc, and hands the line materials the render size when it changed.
 */
export function gizmoView(scene: Scene): GizmoView {
  const nodes = gizmoNodes(scene);
  const sets: Record<GizmoFrame['tool'], HandleSet> = { move: moveHandles(scene, nodes), rotate: rotateHandles(scene, nodes), scale: scaleHandles(scene, nodes) };
  const arc = arcSector(scene, nodes);
  const lines = Object.values(sets).flatMap((s) => s.lines);
  const engine = scene.getEngine();
  const size = new Vector2(0, 0);
  let active: GizmoFrame['tool'] | null | undefined;
  const enable = (tool: GizmoFrame['tool'] | null): void => {
    if (tool === active) return;
    for (const [name, set] of Object.entries(sets)) set.setEnabled(name === tool);
    active = tool;
  };
  enable(null);
  return {
    update: (f) => {
      const w = engine.getRenderWidth();
      const h = engine.getRenderHeight();
      if (w !== size.x || h !== size.y) { size.set(w, h); for (const l of lines) l.resize(size); }
      enable(f?.tool ?? null);
      arc.update(f);
      if (f === null) return;
      nodes.place(f.model, f.size, f.view);
      sets[f.tool].update(f);
    },
    dispose: () => { enable(null); nodes.dispose(); },
  };
}
